import { useEffect } from 'react';

/**
 * Hook pour mesurer et optimiser les Core Web Vitals
 * Envoie les métriques à Google Analytics
 */
export const usePerformance = () => {
  
  useEffect(() => {
    // Vérifier si web-vitals est disponible
    if (typeof window === 'undefined') return;

    // Fonction pour envoyer les métriques
    const sendToAnalytics = (metric) => {
      // Envoyer à Google Analytics si disponible
      if (typeof gtag !== 'undefined') {
        gtag('event', metric.name, {
          value: Math.round(metric.name === 'CLS' ? metric.value * 1000 : metric.value),
          event_category: 'Web Vitals',
          event_label: metric.id,
          non_interaction: true,
        });
      }

      // Envoyer à PostHog si disponible
      if (typeof posthog !== 'undefined') {
        posthog.capture('web_vital', {
          metric_name: metric.name,
          value: metric.value,
          id: metric.id,
          delta: metric.delta,
        });
      }

      // Log pour développement
      if (process.env.NODE_ENV === 'development') {
        console.log(`${metric.name}:`, metric);
      }
    };

    // Mesurer les Core Web Vitals via l'API Performance Observer
    const measureWebVitals = () => {
      // LCP - Largest Contentful Paint
      try {
        const observer = new PerformanceObserver((list) => {
          const entries = list.getEntries();
          const lastEntry = entries[entries.length - 1];
          
          const metric = {
            name: 'LCP',
            value: lastEntry.startTime,
            id: Math.random().toString(36).substr(2, 9),
            delta: lastEntry.startTime
          };
          
          sendToAnalytics(metric);
        });
        
        observer.observe({ entryTypes: ['largest-contentful-paint'] });
        
        // Nettoyer après 30 secondes
        setTimeout(() => observer.disconnect(), 30000);
      } catch (e) {
        // Browser ne supporte pas LCP
      }

      // FID - First Input Delay
      try {
        const observer = new PerformanceObserver((list) => {
          const entries = list.getEntries();
          entries.forEach((entry) => {
            const metric = {
              name: 'FID',
              value: entry.processingStart - entry.startTime,
              id: Math.random().toString(36).substr(2, 9),
              delta: entry.processingStart - entry.startTime
            };
            
            sendToAnalytics(metric);
          });
        });
        
        observer.observe({ entryTypes: ['first-input'] });
      } catch (e) {
        // Browser ne supporte pas FID
      }

      // CLS - Cumulative Layout Shift
      try {
        let clsValue = 0;
        let clsEntries = [];
        
        const observer = new PerformanceObserver((list) => {
          const entries = list.getEntries();
          
          entries.forEach((entry) => {
            if (!entry.hadRecentInput) {
              clsValue += entry.value;
              clsEntries.push(entry);
            }
          });
        });
        
        observer.observe({ entryTypes: ['layout-shift'] });
        
        // Envoyer CLS après 5 secondes
        setTimeout(() => {
          if (clsValue > 0) {
            const metric = {
              name: 'CLS',
              value: clsValue,
              id: Math.random().toString(36).substr(2, 9),
              delta: clsValue,
              entries: clsEntries
            };
            
            sendToAnalytics(metric);
          }
          observer.disconnect();
        }, 5000);
      } catch (e) {
        // Browser ne supporte pas CLS
      }
    };

    // Mesurer les métriques de navigation
    const measureNavigationTiming = () => {
      if (performance.getEntriesByType) {
        const navigation = performance.getEntriesByType('navigation')[0];
        
        if (navigation) {
          // Time to First Byte
          const ttfb = navigation.responseStart - navigation.requestStart;
          
          // DOM Content Loaded
          const dcl = navigation.domContentLoadedEventEnd - navigation.domContentLoadedEventStart;
          
          // Load Complete
          const loadComplete = navigation.loadEventEnd - navigation.navigationStart;
          
          // Envoyer les métriques
          if (typeof gtag !== 'undefined') {
            gtag('event', 'page_timing', {
              ttfb: Math.round(ttfb),
              dcl: Math.round(dcl),
              load_complete: Math.round(loadComplete)
            });
          }
          
          if (process.env.NODE_ENV === 'development') {
            console.log('Navigation Timing:', {
              ttfb: `${Math.round(ttfb)}ms`,
              dcl: `${Math.round(dcl)}ms`,
              loadComplete: `${Math.round(loadComplete)}ms`
            });
          }
        }
      }
    };

    // Démarrer les mesures après le chargement
    if (document.readyState === 'complete') {
      measureWebVitals();
      measureNavigationTiming();
    } else {
      window.addEventListener('load', () => {
        measureWebVitals();
        measureNavigationTiming();
      });
    }

    // Nettoyer les event listeners
    return () => {
      // Les observers sont automatiquement nettoyés
    };
  }, []);
};

/**
 * Hook pour précharger les ressources critiques
 */
export const usePreload = (resources = []) => {
  useEffect(() => {
    resources.forEach(resource => {
      const link = document.createElement('link');
      link.rel = 'preload';
      link.href = resource.href;
      link.as = resource.as || 'image';
      
      if (resource.type) {
        link.type = resource.type;
      }
      
      document.head.appendChild(link);
    });
  }, [resources]);
};

/**
 * Hook pour la gestion du cache et du prefetch
 */
export const usePrefetch = () => {
  const prefetchPage = (url) => {
    const link = document.createElement('link');
    link.rel = 'prefetch';
    link.href = url;
    document.head.appendChild(link);
  };

  const preconnect = (origin) => {
    const link = document.createElement('link');
    link.rel = 'preconnect';
    link.href = origin;
    document.head.appendChild(link);
  };

  return { prefetchPage, preconnect };
};

export default usePerformance;